import React, { useState, useEffect, useRef } from 'react';
import TodoRegisterLayer from '../../../components/todo/TodoRegisterLayer';
import { workOrderApi } from '../../../api/workOrderApi';
import './WorkOrderFeedbackTab.css';

const AVATAR_COLORS = [
  '#2196F3', '#9C27B0', '#FF9800', '#4CAF50', '#00BCD4',
  '#3F51B5', '#009688', '#FFC107', '#795548', '#607D8B'
];

const getAvatarColorInfo = (name) => {
  if (!name) return { bg: '#2196F3', text: 'light' };
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  hash = Math.abs(Math.imul(hash, 2654435761));
  const index = hash % AVATAR_COLORS.length;
  const color = AVATAR_COLORS[index];
  const isDarkText = ['#FFC107', '#FF9800', '#00BCD4', '#4CAF50'].includes(color);
  return { bg: color, text: isDarkText ? 'dark' : 'light' };
};

const Avatar = ({ name = '사용자', photo, size = 32 }) => {
  const [imgError, setImgError] = useState(false);
  const info = getAvatarColorInfo(name);
  const initial = name ? name.charAt(0) : '?';
  const showPhoto = photo && !imgError;

  return (
    <div
      className={`d-flex align-items-center justify-content-center text-${info.text} rounded-circle fw-bolder shadow-sm flex-shrink-0 overflow-hidden`}
      style={{
        width: `${size}px`,
        height: `${size}px`,
        backgroundColor: showPhoto ? 'transparent' : info.bg,
        fontSize: `${size * 0.4}px`,
        userSelect: 'none'
      }}
    >
      {showPhoto ? (
        <img
          src={photo}
          alt={name}
          onError={() => setImgError(true)}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : initial}
    </div>
  );
};

const getRelativeTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  const now = new Date();
  const diffMs = now - date;
  if (diffMs < 0) return '방금 전';
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffMin < 1) return '방금 전';
  if (diffHour < 1) return `${diffMin}분 전`;
  if (diffDay < 1) return `${diffHour}시간 전`;
  if (diffDay < 7) return `${diffDay}일 전`;

  return date.toLocaleString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
};

const checkIsFile = (c) => {
  if (!c) return false;
  if (c.file || c.file_path || c.file_name || c.attached_file_id) return true;
  if (c.files && c.files.length > 0) return true;
  return c.content && /\.(gif|jpg|jpeg|png|pdf|zip|doc|docx|xls|xlsx|ppt|pptx|txt|hwp|csv|ai|psd)$/i.test(String(c.content).trim());
};

export default function WorkOrderFeedbackTab({
  workOrder,
  comments = [],
  currentUser,
  onRefresh,
  focusId,
  isCc = false
}) {
  const isClosed = ['done', 'completed', 'cancel', 'canceled'].includes(workOrder?.status);
  const [inputText, setInputText] = useState('');
  const [commentType, setCommentType] = useState('general'); // general, request
  const [targetUserId, setTargetUserId] = useState([]);
  const [showMemberPopover, setShowMemberPopover] = useState(false);
  const [filterType, setFilterType] = useState('all'); // all, general, notice, request, system
  const [sending, setSending] = useState(false);

  const [replyTarget, setReplyTarget] = useState(null); // { parentId, mentionName }
  const [replyText, setReplyText] = useState('');

  // ── 💡 알림 클릭 시 포커스 스크롤 및 테두리 깜빡임 상태 ──
  const scrollContainerRef = useRef(null);
  const [highlightFeedId, setHighlightFeedId] = useState(null);
  const focusHandledRef = useRef(null);

  useEffect(() => {
    if (focusId && comments && comments.length > 0 && focusHandledRef.current !== String(focusId)) {
      // 💡 포커스된 피드의 유형(일반 또는 요청)을 하단 작성 폼에 자동 동기화
      const targetComment = comments.find(c => String(c.id) === String(focusId));
      if (targetComment) {
        const feedType = targetComment.comment_type === 'request' ? 'request' : 'general';
        setCommentType(feedType);
      }

      let attempts = 0;
      const tryScroll = setInterval(() => {
        attempts++;
        const targetEl = document.getElementById(`feed-${focusId}`);
        const container = scrollContainerRef.current;

        if (targetEl && container && container.clientHeight > 0) {
          clearInterval(tryScroll);
          requestAnimationFrame(() => {
            const contRect = container.getBoundingClientRect();
            const targetRect = targetEl.getBoundingClientRect();
            container.scrollTop = container.scrollTop + (targetRect.top - contRect.top) - (contRect.height / 2) + (targetRect.height / 2);
            focusHandledRef.current = String(focusId);
            setHighlightFeedId(String(focusId));
            setTimeout(() => {
              setHighlightFeedId(null);
            }, 3100);
          });
        }
        if (attempts >= 30) clearInterval(tryScroll);
      }, 100);

      return () => clearInterval(tryScroll);
    }
  }, [focusId, comments]);

  // To-Do 팝업
  const [todoAnchorEl, setTodoAnchorEl] = useState(null);
  const [todoSource, setTodoSource] = useState(null);

  // 대상자 초기화
  useEffect(() => {
    if (commentType !== 'request') {
      setTargetUserId([]);
      setShowMemberPopover(false);
    }
  }, [commentType]);

  // 후보 대상자들 (담당자들 및 지시자 중 본인 제외)
  const assignees = (workOrder?.assignees || []).map(a => a.user).filter(Boolean);
  let candidates = [...assignees];
  if (workOrder?.author && !candidates.some(u => u.id === workOrder.author.id)) {
    candidates.unshift(workOrder.author);
  }
  candidates = candidates.filter(u => u.id !== currentUser?.id);

  const toggleTargetSelection = (uid) => {
    setTargetUserId(prev => {
      if (prev.includes(uid)) return prev.filter(id => id !== uid);
      return [...prev, uid];
    });
  };

  const handleCreateFeed = async () => {
    if (!inputText.trim()) {
      alert('피드 내용을 입력하세요.');
      return;
    }
    try {
      setSending(true);
      const data = {
        content: inputText.trim(),
        source_tab: 'feedback',
        comment_type: commentType,
        target_user_id: (commentType === 'request' && targetUserId.length > 0) ? JSON.stringify(targetUserId) : null,
        parent_id: null
      };

      const res = await workOrderApi.addComment(workOrder.id, data);
      if (res.success) {
        setInputText('');
        setCommentType('request');
        setTargetUserId([]);
        if (onRefresh) onRefresh();
      } else {
        alert(res.message || '피드 등록 실패');
      }
    } catch (err) {
      console.error(err);
      alert('피드 등록 중 오류가 발생했습니다.');
    } finally {
      setSending(false);
    }
  };

  const handleInlineReplySubmit = async (parentId) => {
    if (!replyText.trim()) return;
    try {
      const data = {
        content: replyText.trim(),
        source_tab: 'feedback',
        comment_type: 'general',
        parent_id: parentId,
        target_mention: replyTarget?.mentionName || null
      };

      const res = await workOrderApi.addComment(workOrder.id, data);
      if (res.success) {
        setReplyText('');
        setReplyTarget(null);
        if (onRefresh) onRefresh();
      } else {
        alert(res.message || '답글 등록 실패');
      }
    } catch (err) {
      console.error(err);
      alert('답글 등록 중 오류가 발생했습니다.');
    }
  };

  const handleToggleResolve = async (commentId) => {
    try {
      const res = await workOrderApi.toggleCommentResolve(workOrder.id, commentId);
      if (res.success) {
        if (onRefresh) onRefresh();
      } else {
        alert(res.message || '상태 변경 실패');
      }
    } catch (err) {
      console.error(err);
      alert('상태 변경 중 오류가 발생했습니다.');
    }
  };

  const handleDeleteFeed = async (commentId) => {
    if (!window.confirm('피드를 삭제하시겠습니까?')) return;
    try {
      const res = await workOrderApi.deleteComment(workOrder.id, commentId);
      if (res.success) {
        if (onRefresh) onRefresh();
      } else {
        alert(res.message || '삭제 실패');
      }
    } catch (err) {
      console.error(err);
      alert('삭제 중 오류가 발생했습니다.');
    }
  };

  // 피드 필터링 (코멘트 'comment', '업무지시가 발령되었습니다.' 및 파일 완전 배제, 최신 등록순 정렬)
  const rootFeeds = comments
    .filter(c => !c.parent_id && c.comment_type !== 'comment' && !checkIsFile(c))
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  const filteredFeeds = rootFeeds.filter(feed => {
    if (filterType === 'general' && feed.comment_type !== 'general') return false;
    if (filterType === 'notice' && feed.comment_type !== 'notice') return false;
    if (filterType === 'request' && feed.comment_type !== 'request') return false;
    if (filterType === 'system' && feed.comment_type !== 'system') return false;
    if (filterType === 'my_feed' && feed.user_id !== currentUser?.id) return false;
    return true;
  });

  return (
    <div className="d-flex flex-column h-100 bg-white rounded-3 shadow-sm border overflow-hidden position-relative" style={{ width: '100%' }}>
      <style>{`
        /* 3가지 파스텔 컬러 테마 커스텀 버튼 (PMS 100% 동일) */
        .btn-outline-custom-gen {
          --bs-btn-color: #339af0 !important;
          --bs-btn-border-color: #339af0 !important;
          --bs-btn-hover-color: #fff !important;
          --bs-btn-hover-bg: #339af0 !important;
          --bs-btn-hover-border-color: #339af0 !important;
          --bs-btn-active-color: #fff !important;
          --bs-btn-active-bg: #339af0 !important;
          --bs-btn-active-border-color: #339af0 !important;
          color: #339af0 !important;
          border-color: #339af0 !important;
        }
        .btn-check:checked + .btn-outline-custom-gen {
          background-color: #339af0 !important;
          color: #fff !important;
          border-color: #339af0 !important;
        }

        .btn-outline-custom-not {
          --bs-btn-color: #ff922b !important;
          --bs-btn-border-color: #ff922b !important;
          --bs-btn-hover-color: #fff !important;
          --bs-btn-hover-bg: #ff922b !important;
          --bs-btn-hover-border-color: #ff922b !important;
          --bs-btn-active-color: #fff !important;
          --bs-btn-active-bg: #ff922b !important;
          --bs-btn-active-border-color: #ff922b !important;
          color: #ff922b !important;
          border-color: #ff922b !important;
        }
        .btn-check:checked + .btn-outline-custom-not {
          background-color: #ff922b !important;
          color: #fff !important;
          border-color: #ff922b !important;
        }

        .btn-outline-custom-req {
          --bs-btn-color: #20c997 !important;
          --bs-btn-border-color: #20c997 !important;
          --bs-btn-hover-color: #fff !important;
          --bs-btn-hover-bg: #20c997 !important;
          --bs-btn-hover-border-color: #20c997 !important;
          --bs-btn-active-color: #fff !important;
          --bs-btn-active-bg: #20c997 !important;
          --bs-btn-active-border-color: #20c997 !important;
          color: #20c997 !important;
          border-color: #20c997 !important;
        }
        .btn-check:checked + .btn-outline-custom-req {
          background-color: #20c997 !important;
          color: #fff !important;
          border-color: #20c997 !important;
        }
      `}</style>
      {/* ── 1. 헤더 (PMS 100% 동일 UI) ── */}
      <div className="p-3 border-bottom d-flex align-items-center justify-content-between bg-white position-relative flex-shrink-0">
        <div className="d-flex align-items-center gap-3">
          <h5 className="m-0 fw-bolder text-dark text-nowrap">
            <i className="fas fa-comments text-primary me-2" />피드 라운지
          </h5>
        </div>

        <div className="d-flex align-items-center gap-2">
          <select
            className="form-select form-select-sm shadow-sm border-secondary border-opacity-25 fw-bold text-secondary"
            style={{ width: '110px', cursor: 'pointer' }}
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
          >
            <option value="all">유형: 전체</option>
            <option value="general">일반</option>
            <option value="request">요청</option>
            <option value="my_feed">내 등록 피드</option>
          </select>
        </div>
      </div>

      {/* ── 2. 타임라인 피드 목록 (PMS 100% 동일 카드 테마) ── */}
      <div className="flex-grow-1 px-3 pt-3 overflow-auto" ref={scrollContainerRef}>
        <div style={{ paddingBottom: '12px' }}>
          {filteredFeeds.length === 0 ? (
            <div className="h-100 d-flex flex-column align-items-center justify-content-center text-muted py-5" style={{ minHeight: '260px' }}>
              <div className="mb-3" style={{ fontSize: '48px', color: '#dee2e6' }}>
                <i className="fas fa-comment-dots" />
              </div>
              <h6 className="fw-bolder text-secondary mb-1">아직 등록된 피드가 없습니다.</h6>
              <p className="small mb-0 text-muted">첫 번째 의견이나 요청을 남겨보세요!</p>
            </div>
          ) : (
            filteredFeeds.map((root, index) => {
              const replies = comments
                .filter(c => String(c.parent_id) === String(root.id))
                .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

              const isRequest = root.comment_type === 'request';
              const isNotice = root.comment_type === 'notice';

              let rootBgColor = '#e7f5ff'; let rootBorder = '1px solid #a5d8ff'; let rootOpacity = 1; // 일반
              if (isNotice) {
                rootBgColor = '#fff4e6'; rootBorder = '1px solid #ffd8a8'; // 공지
              } else if (isRequest && !root.is_resolved) {
                rootBgColor = '#e6fcf5'; rootBorder = '1px solid #96f2d7'; // 요청
              } else if (isRequest && root.is_resolved) {
                rootBgColor = '#f1f3f5'; rootBorder = '1px solid #ced4da'; // 해결됨
                rootOpacity = 0.8;
              }

              const authorName = root.user?.name || '사용자';
              const authorPos = root.user?.org?.position || root.user?.position ? ` ${root.user?.org?.position || root.user?.position}` : '';

              let targetIds = [];
              if (Array.isArray(root.target_user_id)) targetIds = root.target_user_id;
              else if (typeof root.target_user_id === 'string') {
                try { targetIds = JSON.parse(root.target_user_id); } catch (e) { targetIds = []; }
              }
              const targetUsers = targetIds.map(id => candidates.find(c => c.id === id)).filter(Boolean);
              let targetText = '전체';
              if (targetUsers.length === 1) targetText = `@${targetUsers[0].name}`;
              else if (targetUsers.length > 1) targetText = `@${targetUsers[0].name} 외 ${targetUsers.length - 1}명`;

              const isAuthorOrOrderer = (currentUser?.id === root.user_id) || (currentUser?.id === workOrder?.author_id);
              const isTarget = targetIds.includes(currentUser?.id);

              return (
                <div
                  id={`feed-${root.id}`}
                  key={root.id}
                  className={`rounded-4 ${index === filteredFeeds.length - 1 ? 'mb-0' : 'mb-3'} ${highlightFeedId === String(root.id) ? 'highlight-feed' : ''}`}
                  style={{ border: rootBorder, opacity: rootOpacity }}
                >
                  {/* 본문 (Root) - 상단 컬러 헤더 */}
                  <div
                    className="py-2 px-3 feedback-comment-row position-relative"
                    style={{
                      backgroundColor: rootBgColor,
                      borderTopLeftRadius: 'inherit',
                      borderTopRightRadius: 'inherit',
                      borderBottomLeftRadius: replies.length === 0 ? 'inherit' : 0,
                      borderBottomRightRadius: replies.length === 0 ? 'inherit' : 0
                    }}
                  >
                    <div className="d-flex align-items-start">
                      <Avatar name={authorName} photo={root.user?.org?.photo_path} size={30} />

                      <div className="ms-3 flex-grow-1">
                        {/* 상단 뱃지 및 메타 정보 */}
                        <div className="d-flex align-items-center mb-1">
                          <div className="d-flex align-items-center gap-2">
                            {isNotice && (
                              <span className="badge px-2 py-1 flex-shrink-0" style={{ fontSize: '11px', backgroundColor: '#f59f00', color: '#fff' }}>
                                <i className="fas fa-bullhorn me-1" />공지
                              </span>
                            )}
                            {isRequest && (
                              <span className="badge px-2 py-1 text-truncate flex-shrink-0" style={{ fontSize: '11px', maxWidth: '200px', backgroundColor: root.is_resolved ? '#40c057' : '#20c997', color: '#fff' }}>
                                {root.is_resolved ? <><i className="fas fa-check-circle me-1" />처리 완료</> : <><i className="fas fa-tasks me-1" />요청: {targetText}</>}
                              </span>
                            )}
                            {!isNotice && !isRequest && (
                              <span className="badge px-2 py-1 flex-shrink-0" style={{ fontSize: '11px', backgroundColor: '#339af0', color: '#fff' }}>
                                <i className="fas fa-comment-dots me-1" />일반
                              </span>
                            )}
                            <span className="fw-bold text-dark text-nowrap flex-shrink-0" style={{ fontSize: '13px' }}>{authorName}</span>
                            <span className="fw-bold text-dark text-nowrap flex-shrink-0" style={{ fontSize: '12px' }}>{authorPos}</span>
                            <span className="text-muted text-nowrap flex-shrink-0" style={{ fontSize: '11px' }}>{getRelativeTime(root.created_at)}</span>
                          </div>

                          {/* 호버 액션 버튼 */}
                          <div
                            className="comment-actions transition-opacity d-flex gap-1 align-items-center ms-2 opacity-0"
                            style={{ position: 'absolute', top: '4px', right: '12px', backgroundColor: rootBgColor, paddingLeft: '8px', zIndex: 10 }}
                          >
                            {!isCc && isRequest && (isAuthorOrOrderer || isTarget) && (
                              <>
                                {!root.is_resolved ? (
                                  <button
                                    type="button"
                                    className="btn btn-sm py-0 px-2 rounded fw-bold"
                                    style={{ backgroundColor: '#40c057', color: '#fff', fontSize: '12px', height: '24px' }}
                                    onClick={() => handleToggleResolve(root.id)}
                                  >
                                    <i className="fas fa-check me-1" />완료 처리
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary py-0 px-2 rounded fw-bold"
                                    style={{ fontSize: '12px', height: '24px' }}
                                    onClick={() => handleToggleResolve(root.id)}
                                  >
                                    <i className="fas fa-undo me-1" />취소
                                  </button>
                                )}
                              </>
                            )}

                            {!isCc && !isClosed && (
                              <button
                                type="button"
                                className="btn btn-sm text-secondary p-1"
                                onClick={() => {
                                  setReplyTarget({ parentId: root.id, targetId: root.id, mentionName: authorName });
                                  setReplyText('');
                                }}
                                title="답글"
                              >
                                <i className="fas fa-reply fs-6" />
                              </button>
                            )}

                            <button
                              type="button"
                              className="btn btn-sm text-secondary p-1"
                              title="To-Do 등록"
                              onClick={(e) => {
                                e.stopPropagation();
                                setTodoAnchorEl({ current: e.currentTarget });
                                setTodoSource({
                                  ref_type: 'work_order_feed',
                                  ref_table: 'tdx_work_order_comments',
                                  ref_id: root.id,
                                  add_title: workOrder?.title || '업무협조',
                                  add_subtype: 'feed',
                                  add_id1: workOrder?.id,
                                  add_id2: root.id,
                                  title: `[업무협조 피드] ${workOrder?.title || ''}`,
                                  memo: root.content || ''
                                });
                              }}
                            >
                              <i className="fas fa-tasks fs-6" />
                            </button>

                            {!isCc && !isClosed && isAuthorOrOrderer && (
                              <button
                                type="button"
                                className="btn btn-sm text-danger p-1"
                                onClick={() => handleDeleteFeed(root.id)}
                                title="삭제"
                              >
                                <i className="fas fa-trash-alt fs-6" />
                              </button>
                            )}
                          </div>
                        </div>

                        {/* 본문 텍스트 */}
                        <div
                          className={`text-dark lh-base mt-1 ${isRequest && root.is_resolved ? 'text-decoration-line-through text-muted' : ''}`}
                          style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '12px' }}
                        >
                          {root.content}
                        </div>

                        {/* 인라인 답글 입력창 (Root 대상) */}
                        {replyTarget?.targetId === root.id && (
                          <div className="mt-2 pt-2" style={{ borderTop: '1px dashed #ced4da' }}>
                            <div className="d-flex gap-2 align-items-stretch">
                              <textarea
                                className="form-control form-control-sm shadow-none"
                                rows="2"
                                placeholder={`@${replyTarget.mentionName}님에게 답글...`}
                                value={replyText}
                                onChange={(e) => setReplyText(e.target.value)}
                                style={{ resize: 'none', fontSize: '12px', flexGrow: 1 }}
                                autoFocus
                              />
                              <div className="d-flex flex-column gap-1 justify-content-center flex-shrink-0">
                                <button
                                  type="button"
                                  className="btn btn-sm text-white rounded-1 px-3 fw-bold shadow-sm"
                                  style={{ backgroundColor: '#4c6ef5', fontSize: '11px' }}
                                  onClick={() => handleInlineReplySubmit(root.id)}
                                >
                                  저장
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm text-white rounded-1 px-3 fw-bold shadow-sm"
                                  style={{ backgroundColor: '#868e96', fontSize: '11px' }}
                                  onClick={() => setReplyTarget(null)}
                                >
                                  취소
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* 답글 (Replies) - 원문과 이어지는 하단 화이트 바디 */}
                  {replies.length > 0 && (
                    <div
                      className="py-2 pe-3"
                      style={{
                        paddingLeft: '32px',
                        borderTop: rootBorder,
                        backgroundColor: '#fdfdfd',
                        borderBottomLeftRadius: 'inherit',
                        borderBottomRightRadius: 'inherit'
                      }}
                    >
                      {replies.map((reply, rIdx) => (
                        <div
                          id={`feed-${reply.id}`}
                          key={reply.id}
                          className={`py-1 feedback-comment-row position-relative d-flex ${rIdx > 0 ? 'mt-1' : ''} ${highlightFeedId === String(reply.id) ? 'highlight-feed' : ''}`}
                        >
                          <div className="reply-connector" style={{ left: '-12px', width: '12px' }} />
                          <Avatar name={reply.user?.name || '사용자'} photo={reply.user?.org?.photo_path} size={26} />

                          <div className="ms-3 flex-grow-1 min-w-0">
                            <div className="d-flex align-items-center mb-1">
                              <div className="d-flex align-items-center gap-2">
                                <span className="fw-bold text-dark text-nowrap flex-shrink-0" style={{ fontSize: '13px' }}>{reply.user?.name || '사용자'}</span>
                                <span className="fw-bold text-dark text-nowrap flex-shrink-0" style={{ fontSize: '12px' }}>{reply.user?.org?.position || reply.user?.position || ''}</span>
                                <span className="text-muted text-nowrap flex-shrink-0" style={{ fontSize: '11px' }}>{getRelativeTime(reply.created_at)}</span>
                              </div>

                              <div
                                className="comment-actions transition-opacity d-flex gap-1 align-items-center ms-2 opacity-0"
                                style={{ position: 'absolute', top: '2px', right: '12px', backgroundColor: '#fdfdfd', paddingLeft: '8px', zIndex: 10 }}
                              >
                                {!isCc && !isClosed && (
                                  <button
                                    type="button"
                                    className="btn btn-sm text-secondary p-0 px-1"
                                    onClick={() => {
                                      setReplyTarget({ parentId: root.id, targetId: reply.id, mentionName: reply.user?.name });
                                      setReplyText('');
                                    }}
                                    title="답글"
                                  >
                                    <i className="fas fa-reply fs-7" />
                                  </button>
                                )}
                                <button
                                  type="button"
                                  className="btn btn-sm text-secondary p-0 px-1"
                                  title="To-Do 등록"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setTodoAnchorEl({ current: e.currentTarget });
                                    setTodoSource({
                                      ref_type: 'work_order_feed',
                                      ref_table: 'tdx_work_order_comments',
                                      ref_id: reply.id,
                                      add_title: workOrder?.title || '업무협조',
                                      add_subtype: 'feed',
                                      add_id1: workOrder?.id,
                                      add_id2: reply.id,
                                      title: `[업무협조 피드] ${workOrder?.title || ''}`,
                                      memo: reply.content || ''
                                    });
                                  }}
                                >
                                  <i className="fas fa-tasks fs-7" />
                                </button>
                                {!isCc && !isClosed && (currentUser?.id === reply.user_id || currentUser?.id === workOrder?.author_id) && (
                                  <button
                                    type="button"
                                    className="btn btn-sm text-danger p-0 px-1"
                                    onClick={() => handleDeleteFeed(reply.id)}
                                    title="삭제"
                                  >
                                    <i className="fas fa-trash-alt fs-7" />
                                  </button>
                                )}
                              </div>
                            </div>

                            <div className="text-dark lh-base mt-1" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '12px' }}>
                              {reply.target_mention && <span className="text-primary fw-bold me-1">@{reply.target_mention}</span>}
                              {reply.content}
                            </div>

                            {/* 인라인 답글 입력창 (Reply 대상) */}
                            {replyTarget?.targetId === reply.id && (
                              <div className="mt-2 pt-2" style={{ borderTop: '1px dashed #ced4da' }}>
                                <div className="d-flex gap-2 align-items-stretch">
                                  <textarea
                                    className="form-control form-control-sm shadow-none"
                                    rows="2"
                                    placeholder={`@${replyTarget.mentionName}님에게 답글...`}
                                    value={replyText}
                                    onChange={(e) => setReplyText(e.target.value)}
                                    style={{ resize: 'none', fontSize: '12px', flexGrow: 1 }}
                                    autoFocus
                                  />
                                  <div className="d-flex flex-column gap-1 justify-content-center flex-shrink-0">
                                    <button
                                      type="button"
                                      className="btn btn-sm text-white rounded-1 px-3 fw-bold shadow-sm"
                                      style={{ backgroundColor: '#4c6ef5', fontSize: '11px' }}
                                      onClick={() => handleInlineReplySubmit(root.id)}
                                    >
                                      저장
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-sm text-white rounded-1 px-3 fw-bold shadow-sm"
                                      style={{ backgroundColor: '#868e96', fontSize: '11px' }}
                                      onClick={() => setReplyTarget(null)}
                                    >
                                      취소
                                    </button>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* ── 3. 스마트 입력창 (Slack 스타일 통합 박스 - PMS 100% 동일) ── */}
      {isClosed ? (
        <div className="px-3 py-3 bg-light border-top text-center text-muted flex-shrink-0" style={{ fontSize: '12.5px' }}>
          <i className="fas fa-lock me-1.5 text-secondary" /> 완료 또는 취소된 업무는 피드를 작성할 수 없습니다.
        </div>
      ) : isCc ? (
        <div className="px-3 py-3 bg-light border-top text-center text-muted flex-shrink-0" style={{ fontSize: '12.5px' }}>
          <i className="fas fa-lock me-1.5 text-secondary" /> 참조자는 피드를 열람(조회)만 할 수 있습니다.
        </div>
      ) : (
        <div className="px-3 bg-white border-top feedback-container flex-shrink-0" style={{ paddingTop: '12px', paddingBottom: '12px' }}>
        <div className="unified-input-box d-flex flex-column">
          <textarea
            className="form-control border-0 shadow-none px-3 pt-3 pb-2"
            rows="2"
            placeholder={commentType === 'request' ? '대상을 선택하고 새로운 피드를 입력하세요...' : '새로운 피드를 입력하세요...'}
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            style={{ resize: 'none', backgroundColor: 'transparent', fontSize: '12px' }}
          />

          <div className="d-flex justify-content-between align-items-center px-3 pb-2 pt-2 border-top">
            <div className="d-flex align-items-center gap-2">
              <div className="btn-group btn-group-sm shadow-sm" role="group">
                <input type="radio" className="btn-check" name="wo-feed-type" id="wo-type-general" checked={commentType === 'general'} onChange={() => setCommentType('general')} />
                <label className="btn btn-outline-custom-gen mb-0 cursor-pointer" htmlFor="wo-type-general">일반</label>

                <input type="radio" className="btn-check" name="wo-feed-type" id="wo-type-request" checked={commentType === 'request'} onChange={() => setCommentType('request')} />
                <label className="btn btn-outline-custom-req mb-0 cursor-pointer" htmlFor="wo-type-request">요청</label>
              </div>

              {commentType === 'request' && (
                <div className="position-relative">
                  <button
                    type="button"
                    className="btn btn-sm btn-light text-primary fw-bold px-3 py-1 shadow-sm border"
                    onClick={() => setShowMemberPopover(!showMemberPopover)}
                  >
                    {targetUserId.length === 0 ? '✨ 전체 대상' : `담당자 지정 (${targetUserId.length}명)`}
                    <i className="fas fa-chevron-up ms-2" />
                  </button>

                  {showMemberPopover && (
                    <div className="position-absolute shadow-lg bg-white rounded-3 p-0" style={{ bottom: '100%', left: 0, marginBottom: '8px', width: '280px', zIndex: 1050, border: '1px solid #c2d7fa', overflow: 'hidden' }}>
                      <div className="p-2 border-bottom" style={{ backgroundColor: '#e8f0fe' }}>
                        <span className="fw-bold small text-dark"><i className="fas fa-user-check me-1 text-success" />요청 대상자 선택</span>
                      </div>
                      <div className="p-2" style={{ maxHeight: '180px', overflowY: 'auto' }}>
                        {/* 전체 대상 */}
                        <div
                          className="d-flex align-items-center px-2 py-1 mb-1 rounded cursor-pointer"
                          style={{ backgroundColor: targetUserId.length === 0 ? '#e8f0fe' : 'transparent', border: targetUserId.length === 0 ? '1px solid #d2e3fc' : '1px solid transparent' }}
                          onClick={() => setTargetUserId([])}
                        >
                          <div className="flex-shrink-0 me-2">
                            {targetUserId.length === 0 ? (
                              <div className="rounded-circle d-flex align-items-center justify-content-center bg-primary text-white shadow-sm" style={{ width: '22px', height: '22px' }}>
                                <i className="fas fa-check" style={{ fontSize: '10px' }} />
                              </div>
                            ) : (
                              <div className="rounded-circle d-flex align-items-center justify-content-center bg-light text-secondary fw-bold shadow-sm border" style={{ width: '22px', height: '22px', fontSize: '10px' }}>
                                All
                              </div>
                            )}
                          </div>
                          <span className={`fw-bold small ${targetUserId.length === 0 ? 'text-primary' : 'text-dark'}`}>✨ 전체 대상</span>
                        </div>

                        {/* 개별 후보들 */}
                        {candidates.map(u => {
                          const isSelected = targetUserId.includes(u.id);
                          return (
                            <div
                              key={u.id}
                              className="d-flex align-items-center px-2 py-1 mb-1 rounded cursor-pointer"
                              style={{ backgroundColor: isSelected ? '#e8f0fe' : 'transparent', border: isSelected ? '1px solid #d2e3fc' : '1px solid transparent' }}
                              onClick={() => toggleTargetSelection(u.id)}
                            >
                              <div className="flex-shrink-0 me-2">
                                {isSelected ? (
                                  <div className="rounded-circle d-flex align-items-center justify-content-center bg-primary text-white shadow-sm" style={{ width: '22px', height: '22px' }}>
                                    <i className="fas fa-check" style={{ fontSize: '10px' }} />
                                  </div>
                                ) : (
                                  <Avatar name={u.name} photo={u.org?.photo_path} size={22} />
                                )}
                              </div>
                              <span className={`small ${isSelected ? 'text-primary fw-bold' : 'text-dark'}`}>
                                {u.name} {u.org?.position || u.position || ''}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                      <div className="p-2 border-top d-flex justify-content-between align-items-center" style={{ backgroundColor: '#e8f0fe' }}>
                        <span className="small text-muted fw-bold">선택: <strong className="text-primary">{targetUserId.length}명</strong></span>
                        <button type="button" className="btn btn-sm btn-primary py-0 px-2 fw-bold" onClick={() => setShowMemberPopover(false)}>완료</button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <button
              type="button"
              className="btn btn-primary btn-sm px-4 fw-bold shadow-sm rounded"
              style={{ backgroundColor: '#748ffc', borderColor: '#748ffc' }}
              disabled={sending || !inputText.trim()}
              onClick={handleCreateFeed}
            >
              <i className="fas fa-paper-plane me-1" /> 등록
            </button>
          </div>
        </div>
      </div>
    )}

      {/* To-Do 등록 팝업 레이어 */}
      {todoAnchorEl && todoSource && (
        <TodoRegisterLayer
          source={todoSource}
          anchorRef={todoAnchorEl}
          onClose={() => {
            setTodoAnchorEl(null);
            setTodoSource(null);
          }}
          direction="down"
          align="right"
        />
      )}
    </div>
  );
}


